class HeaderCollapsiblePanel extends HTMLElement {
  static instances = [];

  constructor() {
    super();
  }

  connectedCallback() {
    // Register this instance
    HeaderCollapsiblePanel.instances.push(this);

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    // Elements used for JS-driven animation (modeled after CoreDetails)
    this.panel = this.querySelector('[data-collapsible-content]');

    if (!this.panel) {
      console.error('HeaderCollapsiblePanel: Could not find element with [data-collapsible-content]');
      return;
    }

    const targetHeightElement = this.panel.querySelector('[data-collapsible-target-height]');
    if (!targetHeightElement) {
      console.error('HeaderCollapsiblePanel: Could not find element with [data-collapsible-target-height]');
      return;
    }

    this.targetHeight = this.#calculateTargetHeight(targetHeightElement);

    if (!this.detailsContainer || !this.summaryToggle) {
      console.error('HeaderCollapsiblePanel: Could not find required details or summary elements');
      return;
    }

    this.detailsContainer.addEventListener('keyup', (event) => event.code === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    // Capture user gesture early for iOS focus behavior
    this.summaryToggle.addEventListener('pointerdown', this.onSummaryPointerDown.bind(this));

    // Get global overlay element
    this.globalOverlay = document.getElementById('global-overlay');

    // Add click listener to overlay for closing menu
    if (this.globalOverlay) {
      this.globalOverlay.addEventListener('click', () => {
        if (this.isOpen()) {
          this.close();
        }
      });
    }

    // Handle all close buttons (type="button", data-close-button, modal-close-button class)
    this.querySelectorAll('button[type="button"], [data-close-button], .modal-close-button').forEach((e) => {
      e.addEventListener('click', this.close.bind(this));
    });

    // Handle submenu close buttons specifically
    this.querySelectorAll('[data-submenu-close-button]').forEach((button) => {
      button.addEventListener('click', this.onSubmenuCloseButtonClick.bind(this));
    });

    // Handle submenu details elements
    this.querySelectorAll('details').forEach((details) => {
      if (details !== this.detailsContainer) {
        const summary = details.querySelector('summary');
        if (summary) {
          summary.addEventListener('click', this.onSubmenuClick.bind(this));
        }
      }
    });

    this.summaryToggle.setAttribute('role', 'button');
    this.summaryToggle.setAttribute('aria-expanded', 'false');

    this.#initPanelStyles();
  }

  disconnectedCallback() {
    // Remove this instance from the static array
    const index = HeaderCollapsiblePanel.instances.indexOf(this);
    if (index > -1) {
      HeaderCollapsiblePanel.instances.splice(index, 1);
    }
  }

  async closeOtherInstances() {
    const closingPromises = HeaderCollapsiblePanel.instances
      .filter((instance) => instance !== this && instance.isOpen())
      .map((instance) => {
        return new Promise((resolve) => {
          // Close other instances with proper header class management
          instance.closeWithoutHeaderClassRemoval(resolve);
        });
      });

    // Wait for all instances to close
    await Promise.all(closingPromises);
  }

  closeWithoutHeaderClassRemoval(onComplete) {
    removeTrapFocus(false);

    // Replace opened class with closing class for delayed border transition
    const headerEl = this.detailsContainer.closest('.header');
    if (headerEl) {
      headerEl.classList.remove('header-collapsible-panel--opened');
      headerEl.classList.add('header-collapsible-panel--closing');
    }

    // NOTE: Don't hide overlay or remove body classes here -
    // this method is used when switching between panels
    // The new panel will handle overlay and body classes

    // If we have an animatable panel, animate out first, then remove `open`
    if (this.panel && this.detailsContainer.hasAttribute('open')) {
      this.#animateClose(() => {
        this.detailsContainer.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClickEvent);
        this.summaryToggle.setAttribute('aria-expanded', 'false');
        // Remove the closing class after animation completes
        if (headerEl) headerEl.classList.remove('header-collapsible-panel--closing');

        // Only reset Gorgias z-index if no other panels are open
        if (!HeaderCollapsiblePanel.hasOpenPanels()) {
          const gorgiasContainer = document.getElementById('gorgias-chat-container');
          if (gorgiasContainer) {
            gorgiasContainer.style.zIndex = '';
          }
        }

        if (onComplete) onComplete();
      });
      return;
    }

    // Fallback: instant close
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    // Remove the closing class for instant close
    if (headerEl) headerEl.classList.remove('header-collapsible-panel--closing');

    // Only reset Gorgias z-index if no other panels are open
    if (!HeaderCollapsiblePanel.hasOpenPanels()) {
      const gorgiasContainer = document.getElementById('gorgias-chat-container');
      if (gorgiasContainer) {
        gorgiasContainer.style.zIndex = '';
      }
    }

    if (onComplete) onComplete();
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  // Static method to check if any panel is open
  static hasOpenPanels() {
    return HeaderCollapsiblePanel.instances.some((instance) => instance.isOpen());
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onSummaryPointerDown(event) {
    // Mark that the upcoming open was initiated by a direct user gesture
    if (this.classList.contains('header__search') && !this.isOpen()) {
      this._openFromGesture = true;
    }
  }

  onSubmenuClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    if (!isOpen) {
      // Add menu-opening class for slide animation
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
      }, 10);
    } else {
      // Remove menu-opening class when closing
      detailsElement.classList.remove('menu-opening');
    }
  }

  onSubmenuCloseButtonClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const detailsElement = event.currentTarget.closest('details');
    if (detailsElement && detailsElement !== this.detailsContainer) {
      // Start the slide-back animation by removing menu-opening class
      detailsElement.classList.remove('menu-opening');

      // Wait for the animation to complete before removing the open attribute
      const submenu = detailsElement.querySelector('.menu-drawer__submenu');
      if (submenu) {
        const onTransitionEnd = () => {
          detailsElement.removeAttribute('open');
          submenu.removeEventListener('transitionend', onTransitionEnd);
        };
        submenu.addEventListener('transitionend', onTransitionEnd, { once: true });

        // Fallback timeout in case transitionend doesn't fire
        setTimeout(() => {
          detailsElement.removeAttribute('open');
          submenu.removeEventListener('transitionend', onTransitionEnd);
        }, 350);
      } else {
        // Fallback if no submenu element found
        setTimeout(() => {
          detailsElement.removeAttribute('open');
        }, 300);
      }
    }
  }

  onBodyClick(event) {
    if (document.querySelector('header').classList.contains('header-collapsible-panel--opened')) {
      return;
    }
    if (!this.contains(event.target)) this.close(false);
  }

  async open(event) {
    // If invoked immediately after a pointer gesture, open synchronously and focus before any await
    if (this._openFromGesture && !this.isOpen()) {
      this._openFromGesture = false;

      this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
      this.detailsContainer.setAttribute('open', true);
      document.body.addEventListener('click', this.onBodyClickEvent);

      // Ensure body classes are properly set
      document.body.classList.add('overflow-hidden-tablet');

      this.summaryToggle.setAttribute('aria-expanded', 'true');
      const headerEl = this.detailsContainer.closest('.header');
      if (headerEl) {
        headerEl.classList.remove('header-collapsible-panel--closing');
        headerEl.classList.add('header-collapsible-panel--opened');
      }

      // Show global overlay
      if (this.globalOverlay) {
        this.globalOverlay.classList.add('visible');
      }

      // Bring Gorgias above overlay
      const gorgiasContainer = document.getElementById('gorgias-chat-container');
      if (gorgiasContainer) {
        gorgiasContainer.style.zIndex = '2';
      }

      this.#animateOpen();

      // Focus immediately while still in the same user gesture
      if (this.classList.contains('header__search')) {
        const input = this.querySelector('.search__input');
        if (input) {
          try {
            input.focus({ preventScroll: true });
          } catch (e) {}
        }
      }

      // Then close other instances asynchronously
      await this.closeOtherInstances();
      return;
    }

    // Close all other instances and wait for them to finish before opening this one
    await this.closeOtherInstances();

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    // Ensure body classes are properly set (important for panel switching)
    document.body.classList.add('overflow-hidden-tablet');

    this.summaryToggle.setAttribute('aria-expanded', 'true');
    const headerEl = this.detailsContainer.closest('.header');
    if (headerEl) {
      // Clean up any existing closing class and add opened class
      headerEl.classList.remove('header-collapsible-panel--closing');
      headerEl.classList.add('header-collapsible-panel--opened');
    }

    // Show global overlay by adding visible class
    if (this.globalOverlay) {
      this.globalOverlay.classList.add('visible');
    }

    // Set z-index to 2 on Gorgias chat container when opened
    const gorgiasContainer = document.getElementById('gorgias-chat-container');
    if (gorgiasContainer) {
      gorgiasContainer.style.zIndex = '2';
    }
    this.#animateOpen();

    // Fallback focus when not opened from a direct pointer gesture
    if (this.classList.contains('header__search')) {
      const input = this.querySelector('.search__input');
      if (input) {
        // Slight delay to ensure the panel is visible before focusing
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              input.focus({ preventScroll: true });
            } catch (e) {}
          }, 50);
        });
      }
    }
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);

    // Replace opened class with closing class for delayed border transition
    const headerEl = this.detailsContainer.closest('.header');
    if (headerEl) {
      headerEl.classList.remove('header-collapsible-panel--opened');
      headerEl.classList.add('header-collapsible-panel--closing');
    }

    // If we have an animatable panel, animate out first, then remove `open`
    if (this.panel && this.detailsContainer.hasAttribute('open')) {
      this.#animateClose(() => {
        this.detailsContainer.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClickEvent);

        // Only remove body classes and hide overlay if no other panels are open
        if (!HeaderCollapsiblePanel.hasOpenPanels()) {
          document.body.classList.remove('overflow-hidden', 'overflow-hidden-tablet');
          // Hide global overlay only if no panels are open
          if (this.globalOverlay) {
            this.globalOverlay.classList.remove('visible');
          }
        }

        this.summaryToggle.setAttribute('aria-expanded', 'false');
        // Remove the closing class after animation completes
        if (headerEl) headerEl.classList.remove('header-collapsible-panel--closing');

        // Only reset Gorgias z-index if no other panels are open
        if (!HeaderCollapsiblePanel.hasOpenPanels()) {
          const gorgiasContainer = document.getElementById('gorgias-chat-container');
          if (gorgiasContainer) {
            gorgiasContainer.style.zIndex = '';
          }
        }
      });
      return;
    }

    // Fallback: instant close
    this.detailsContainer.removeAttribute('open');
    document.body.removeEventListener('click', this.onBodyClickEvent);

    // Only remove body classes and hide overlay if no other panels are open
    if (!HeaderCollapsiblePanel.hasOpenPanels()) {
      document.body.classList.remove('overflow-hidden', 'overflow-hidden-tablet');
      // Hide global overlay only if no panels are open
      if (this.globalOverlay) {
        this.globalOverlay.classList.remove('visible');
      }
    }

    this.summaryToggle.setAttribute('aria-expanded', 'false');
    // Remove the closing class for instant close
    if (headerEl) headerEl.classList.remove('header-collapsible-panel--closing');

    // Only reset Gorgias z-index if no other panels are open
    if (!HeaderCollapsiblePanel.hasOpenPanels()) {
      const gorgiasContainer = document.getElementById('gorgias-chat-container');
      if (gorgiasContainer) {
        gorgiasContainer.style.zIndex = '';
      }
    }
  }

  #calculateTargetHeight(element) {
    // Try scrollHeight first (works in most browsers)
    let height = element.scrollHeight;

    // Safari fallback: use offsetHeight if scrollHeight is 0
    if (height === 0) {
      height = element.offsetHeight;
    }

    // Final fallback
    return height || 300;
  }

  #initPanelStyles() {
    if (!this.panel) return;
    this.#setupTransition();
    this.panel.style.overflow = 'hidden';
    if (!this.detailsContainer.hasAttribute('open')) {
      this.panel.style.maxHeight = '0';
      this.panel.style.transform = 'translateY(-3.8rem)';
      this.panel.style.pointerEvents = 'none';
    } else {
      this.panel.style.maxHeight = this.targetHeight + 'px';
      this.panel.style.transform = 'translateY(0)';
      this.panel.style.pointerEvents = '';
    }
  }

  #setupTransition() {
    if (!this.panel) return;
    const transition = 'max-height var(--duration-long-cubic) ease, transform var(--duration-long-cubic) ease-out';
    if (this.panel.style.transition !== transition) {
      this.panel.style.transition = transition;
      this.panel.style.willChange = 'max-height, transform';
    }
  }

  #animateOpen() {
    if (!this.panel) return;

    // Recalculate height in case content has changed (especially important for Safari)
    const targetHeightElement = this.panel.querySelector('[data-collapsible-target-height]');
    if (targetHeightElement) {
      this.targetHeight = this.#calculateTargetHeight(targetHeightElement);
    }

    // Prepare start state
    this.panel.style.pointerEvents = '';
    this.panel.style.overflow = 'hidden';
    this.panel.style.maxHeight = '0';
    this.panel.style.transform = 'translateY(-3.8rem)';
    requestAnimationFrame(() => {
      this.panel.style.maxHeight = this.targetHeight + 'px';
      this.panel.style.transform = 'translateY(0)';
    });
  }

  #animateClose(done) {
    // Animate to hidden state, then call done
    this.panel.style.maxHeight = '0';
    this.panel.style.transform = 'translateY(-3.8rem)';
    const onEnd = () => {
      this.panel.style.pointerEvents = 'none';
      this.panel.removeEventListener('transitionend', onEnd);
      if (typeof done === 'function') done();
    };
    this.panel.addEventListener('transitionend', onEnd, { once: true });
  }
}

customElements.define('header-collapsible-panel', HeaderCollapsiblePanel);
